/*
 * Native theme forms submitted to the HubSpot Forms API v3 ([data-atm-hs-form]).
 * Without data-form-guid the form keeps its native Shopify contact submit, so a form
 * can ship before its HubSpot definition exists. Hidden context is filled in both modes.
 * Only fields present in the HubSpot form definition may be sent (data-hs-name).
 */
// Several sections can include this file on one page; run it once.
if (!window.AtmHsFormLoaded) {
window.AtmHsFormLoaded = true;

// HubSpot country standard (2026-10-10): English names as in the country_rfq options.
// Intl gives English names; these codes are spelled differently in the standard.
const ATM_COUNTRY_STANDARD = {
  AG: 'Antigua and Barbuda', BA: 'Bosnia and Herzegovina', BL: 'Saint Barthélemy', CD: 'Democratic Republic of the Congo',
  CG: 'Congo', CI: "Cote d'Ivoire", CZ: 'Czech Republic', GS: 'South Georgia and the South Sandwich Islands', HK: 'Hong Kong',
  HM: 'Heard Island and McDonald Islands', KN: 'Saint Kitts and Nevis', LC: 'Saint Lucia', MF: 'Saint Martin',
  MK: 'Macedonia (FYROM)', MO: 'Macau', PM: 'Saint Pierre and Miquelon', PS: 'Palestine', SH: 'Saint Helena',
  SJ: 'Svalbard and Jan Mayen', ST: 'Sao Tome and Principe', SZ: 'Swaziland', TC: 'Turks and Caicos Islands',
  TF: 'French Southern and Antarctic Lands', TL: 'East Timor', TT: 'Trinidad and Tobago',
  UM: 'United States Minor Outlying Islands', VC: 'Saint Vincent and the Grenadines', WF: 'Wallis and Futuna',
};

function atmCountryEnglish(iso) {
  if (!iso) return '';
  if (ATM_COUNTRY_STANDARD[iso]) return ATM_COUNTRY_STANDARD[iso];
  try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso) || ''; } catch (e) { return ''; }
}

class AtmHsForm {
  static MIN_FILL_MS = 3000;

  constructor(form) {
    this.form = form;
    this.startedAt = Date.now();
    this.status = form.querySelector('[data-atm-hs-status]');
    this.submit = form.querySelector('[type="submit"]');
    // Product pages pass their own context (SKU, product, URL); the referrer must not overwrite it.
    if (form.dataset.atmHsPrefill !== 'off') this.prefill();
    if (form.dataset.atmHsMode === 'withdrawal') {
      form.addEventListener('atm-withdrawal-open', () => { this.startedAt = Date.now(); });
    }
    if (form.dataset.formGuid) form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  field(name) {
    return this.form.querySelector(`[data-hs-name="${name}"]`);
  }

  setValue(name, value, overwrite = false) {
    const input = this.field(name);
    if (input && value && (overwrite || !input.value)) input.value = value;
  }

  async prefill() {
    const params = new URLSearchParams(window.location.search);
    const sku = (params.get('sku') || '').trim();
    const handle = (params.get('product') || '').trim();
    const variantId = params.get('variant');
    this.setValue('part_number', sku);
    this.setValue('web_source_sku', sku, true);
    this.setValue('web_source_url', document.referrer.startsWith(window.location.origin) ? document.referrer : '', true);
    if (!sku && !handle) return;

    const root = this.form.dataset.rootUrl || '/';
    const url = handle
      ? `${root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?view=rfq-context`
      : `${root.replace(/\/$/, '')}/search?type=product&options%5Bfields%5D=variants.sku&view=rfq-context&q=${encodeURIComponent(sku)}`;
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(String(response.status));
      const variants = JSON.parse(await response.text());
      const match = variants.find((v) => (sku && String(v.sku).toLowerCase() === sku.toLowerCase()) || (variantId && String(v.id) === variantId))
        || (handle && !sku ? variants[0] : null);
      if (!match) {
        if (sku) this.setValue('web_source_availability', 'not_on_shop', true);
        return;
      }
      this.setValue('part_number', match.sku);
      this.setValue('web_source_sku', match.sku, true);
      this.setValue('web_source_product', match.product, true);
      this.setValue('web_source_url', match.url, true);
      this.setValue('web_source_availability', match.availability, true);
      this.setValue('web_source_install_policy', match.install_policy, true);
      this.showProduct(match);
    } catch (error) {
      // Context is best effort; the customer can always submit.
    }
  }

  // RFQ from a product page: show what is being quoted instead of an editable P/N field.
  showProduct(match) {
    const card = this.form.querySelector('[data-atm-rfq-product]');
    if (!card || !match.product) return;
    card.querySelector('[data-atm-rfq-product-title]').textContent = match.product;
    card.querySelector('[data-atm-rfq-product-pn]').textContent = match.sku || '';
    const img = card.querySelector('[data-atm-rfq-product-img]');
    if (img && match.image) { img.src = match.image; img.hidden = false; }
    card.hidden = false;
    const pnField = this.form.querySelector('[data-atm-rfq-pn-field]');
    if (pnField && this.field('part_number')?.value) pnField.hidden = true;
  }

  message(name) {
    return this.form.querySelector(`[data-atm-hs-msg="${name}"]`)?.textContent.trim() || '';
  }

  setStatus(message, state) {
    this.status.textContent = message;
    this.status.dataset.state = state;
    this.status.hidden = !message;
    if (message) this.status.focus({ preventScroll: false });
  }

  clearFieldErrors() {
    this.form.querySelectorAll('[aria-invalid="true"]').forEach((input) => input.removeAttribute('aria-invalid'));
    this.form.querySelectorAll('[data-atm-hs-field-error]').forEach((node) => node.remove());
  }

  markField(name) {
    const input = this.field(name) || (name === 'firstname' || name === 'lastname' ? this.field('fullname') : null);
    if (!input || input.type === 'hidden') return false;
    input.setAttribute('aria-invalid', 'true');
    const note = document.createElement('span');
    note.className = 'atm-hs-form__field-error';
    note.dataset.atmHsFieldError = '';
    note.textContent = this.message('field');
    input.insertAdjacentElement('afterend', note);
    return true;
  }

  fieldValue(input) {
    const value = input.value.trim();
    // Prefilled shop country is in the shop language; HubSpot stores the English standard name.
    if (input.dataset.countryIso && value === (input.dataset.countryDefault || '').trim()) {
      return atmCountryEnglish(input.dataset.countryIso) || value;
    }
    if (input.type === 'date' && value) {
      const [y, m, d] = value.split('-').map(Number);
      return String(Date.UTC(y, m - 1, d));
    }
    return value;
  }

  fields() {
    const out = [];
    this.form.querySelectorAll('[data-hs-name]').forEach((input) => {
      const name = input.dataset.hsName;
      const value = this.fieldValue(input);
      if (!value) return;
      if (name === 'fullname') {
        const parts = value.split(/\s+/);
        out.push({ name: 'firstname', value: parts.shift() });
        if (parts.length) out.push({ name: 'lastname', value: parts.join(' ') });
        return;
      }
      out.push({ name, value });
    });
    return out;
  }

  // Unticked marketing opt-in (atm-native-form, 2026-10-28). Only a ticked box subscribes the contact to marketing
  // e-mail; an enquiry alone does not. shop_email_marketing_consent must exist in the HubSpot form definition.
  withConsent(body) {
    const box = this.form.querySelector('[data-atm-hs-consent]');
    if (!box || !box.checked) return body;
    const text = box.dataset.consentText;
    // Comma-separated: offers, avionics, safety (the consent text covers all three).
    const communications = String(box.dataset.subscriptionTypeId || '')
      .split(',')
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0)
      .map((subscriptionTypeId) => ({ value: true, subscriptionTypeId, text }));
    body.fields.push({ name: 'shop_email_marketing_consent', value: 'true' });
    body.legalConsentOptions = { consent: { consentToProcess: true, text, communications } };
    return body;
  }

  hutk() {
    const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  async handleSubmit(event) {
    event.preventDefault();
    // Shopify's captcha bootstrap listens on document and re-submits every form 'contact'
    // natively, which sends a duplicate "New customer message" e-mail. HubSpot is the only target here.
    event.stopImmediatePropagation();
    this.clearFieldErrors();
    this.setStatus('', '');

    const withdrawal = this.form.dataset.atmHsMode === 'withdrawal';
    const honeypot = this.form.querySelector('[data-atm-hs-honeypot]');
    if (honeypot && honeypot.value) {
      if (withdrawal) {
        this.setStatus(this.message('error'), 'error');
        return;
      }
      this.done();
      return;
    }
    if (Date.now() - this.startedAt < AtmHsForm.MIN_FILL_MS) {
      if (withdrawal) {
        this.setStatus(this.message('too_fast'), 'error');
        return;
      }
      this.done();
      return;
    }

    const context = { pageUri: window.location.href, pageName: document.title };
    const hutk = this.hutk();
    // A withdrawal is a legal declaration, not a marketing/contact-identification
    // event. Do not let a HubSpot tracking cookie associate it with another person
    // on a shared device.
    if (hutk && !withdrawal) context.hutk = hutk;

    this.submit.disabled = true;
    this.form.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${encodeURIComponent(this.form.dataset.portalId)}/${encodeURIComponent(this.form.dataset.formGuid)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.withConsent({ fields: this.fields(), context })),
        }
      );
      if (response.ok) {
        this.done(new Date());
        return;
      }
      const body = await response.json().catch(() => ({}));
      let marked = false;
      (body.errors || []).forEach((error) => {
        const name = /fields\.([\w-]+)/.exec(error.message || '')?.[1];
        if (name && this.markField(name)) marked = true;
      });
      this.setStatus(this.message(marked ? 'invalid' : 'error'), 'error');
    } catch (error) {
      this.setStatus(this.message('error'), 'error');
    } finally {
      this.submit.disabled = false;
      this.form.removeAttribute('aria-busy');
    }
  }

  done(sentAt) {
    if (this.form.dataset.atmHsMode === 'withdrawal') {
      this.showReceipt(sentAt || new Date());
      return;
    }
    this.form.reset();
    this.form.querySelectorAll('[data-atm-hs-fields]').forEach((node) => { node.hidden = true; });
    this.setStatus(this.message('success'), 'success');
  }

  showReceipt(sentAt) {
    const receipt = this.form.querySelector('[data-atm-hs-receipt]');
    const rows = receipt?.querySelector('[data-atm-hs-receipt-rows]');
    const whenEl = receipt?.querySelector('[data-atm-hs-receipt-when]');
    if (!receipt || !rows || !whenEl) {
      this.form.reset();
      this.setStatus(this.message('success'), 'success');
      return;
    }
    const lang = this.form.dataset.locale || document.documentElement.lang || undefined;
    let when = sentAt.toISOString();
    try {
      // dateStyle/timeStyle cannot be combined with timeZoneName (RangeError), so spell the parts out.
      when = new Intl.DateTimeFormat(lang, {
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short',
      }).format(sentAt);
    } catch (error) {
      when = sentAt.toISOString();
    }
    rows.replaceChildren();
    this.form.querySelectorAll('[data-atm-hs-fields] [data-hs-name]').forEach((input) => {
      if (input.type === 'hidden') return;
      const label = input.dataset.receiptLabel || input.dataset.hsName;
      const value = input.value.trim() || this.message('not_provided');
      const dt = document.createElement('dt');
      dt.textContent = label;
      const dd = document.createElement('dd');
      dd.textContent = value;
      rows.append(dt, dd);
    });
    whenEl.textContent = when;
    whenEl.dateTime = sentAt.toISOString();
    this.form.querySelectorAll('[data-atm-hs-fields]').forEach((node) => { node.hidden = true; });
    const root = this.form.closest('[data-atm-withdrawal]');
    const opener = root?.querySelector('[data-atm-withdrawal-open]');
    if (opener) opener.hidden = true;
    const panel = this.form.closest('[data-atm-withdrawal-panel]');
    if (panel) panel.hidden = false;
    receipt.hidden = false;
    this.setStatus('', '');
    receipt.focus();
  }
}

document.querySelectorAll('[data-atm-hs-form]').forEach((form) => new AtmHsForm(form));

document.querySelectorAll('[data-atm-withdrawal-open]').forEach((button) => {
  const panel = document.getElementById(button.getAttribute('aria-controls'));
  const form = panel?.querySelector('[data-atm-hs-form]');
  button.addEventListener('click', () => {
    if (!panel || panel.querySelector('[data-atm-hs-receipt]:not([hidden])')) return;
    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    if (willOpen) {
      form?.dispatchEvent(new CustomEvent('atm-withdrawal-open'));
      panel.querySelector('input:not([type="hidden"]), textarea')?.focus();
    }
  });
});
}
